import { localPreferences } from 'app-components/utils/core';

const DEV_ROUTING_KEY = 'devRoutingConfig';

export type RoutingRule = {
  id: string;
  enabled: boolean;
  pathPrefix: string;
};

export type DevRoutingConfig = {
  globalEnabled: boolean;
  devHostIp: string;
  rules: RoutingRule[];
};

const DEFAULT_CONFIG: DevRoutingConfig = {
  globalEnabled: false,
  devHostIp: '',
  rules: [],
};

export const getDevRoutingConfig = (): DevRoutingConfig => {
  const stored = localPreferences.getItem(DEV_ROUTING_KEY);
  if (!stored) {
    return DEFAULT_CONFIG;
  }
  return {
    ...DEFAULT_CONFIG,
    ...stored,
  };
};

export const setDevRoutingConfig = (config: DevRoutingConfig): void => {
  localPreferences.setItem(DEV_ROUTING_KEY, config);
};

export const setGlobalEnabled = (enabled: boolean): void => {
  const config = getDevRoutingConfig();
  setDevRoutingConfig({ ...config, globalEnabled: enabled });
};

export const setDevHostIp = (ip: string): void => {
  const config = getDevRoutingConfig();
  setDevRoutingConfig({ ...config, devHostIp: ip });
};

export const addRoutingRule = (pathPrefix: string): RoutingRule => {
  const config = getDevRoutingConfig();
  const newRule: RoutingRule = {
    id: `${Date.now()}-${Math.random().toString(36).substring(2, 11)}`,
    enabled: true,
    pathPrefix,
  };
  setDevRoutingConfig({
    ...config,
    rules: [...config.rules, newRule],
  });
  return newRule;
};

export const removeRoutingRule = (id: string): void => {
  const config = getDevRoutingConfig();
  setDevRoutingConfig({
    ...config,
    rules: config.rules.filter((rule) => rule.id !== id),
  });
};

export const toggleRoutingRule = (id: string, enabled: boolean): void => {
  const config = getDevRoutingConfig();
  setDevRoutingConfig({
    ...config,
    rules: config.rules.map((rule) => (rule.id === id ? { ...rule, enabled } : rule)),
  });
};

export const updateRoutingRulePath = (id: string, pathPrefix: string): void => {
  const config = getDevRoutingConfig();
  setDevRoutingConfig({
    ...config,
    rules: config.rules.map((rule) => (rule.id === id ? { ...rule, pathPrefix } : rule)),
  });
};

/**
 * Encodes the devhost IP into the format expected by the gateway routing header.
 * Format: base64 encoded string of `host == "${ip}"`
 */
export const encodeRoutingTarget = (ip: string): string => {
  const routingString = `host == "${ip}"`;
  return btoa(routingString);
};

/**
 * Checks if a given URL path matches any enabled routing rule.
 * Returns the encoded routing header value if a match is found, null otherwise.
 */
export const getRoutingHeaderForPath = (pathname: string): string | null => {
  const config = getDevRoutingConfig();

  if (!config.globalEnabled || !config.devHostIp) {
    return null;
  }

  for (const rule of config.rules) {
    if (!rule.enabled) continue;

    if (pathname.startsWith(rule.pathPrefix)) {
      return encodeRoutingTarget(config.devHostIp);
    }
  }

  return null;
};
